/**
 * VCB — sonda degli errori del sito.
 *
 * Registra in GA4 tre cose che oggi non sappiamo:
 *   vcb_js_error      errori JavaScript e promesse rifiutate
 *   vcb_page_crash    la pagina precedente e' morta senza chiudersi (renderer OOM)
 *   vcb_empty_results collezione o ricerca che non mostra nessun prodotto
 *
 * Perche' il crash si rileva DOPO e non mentre accade: quando il renderer
 * esaurisce la memoria il processo viene ucciso, e con lui il JavaScript che
 * dovrebbe segnalarlo. L'unico modo e' lasciare un segnale all'avvio e
 * cancellarlo su `pagehide` (che scatta a ogni uscita pulita, navigazione o
 * bfcache). Se al caricamento dopo il segnale e' ancora li', la pagina prima e'
 * morta male.
 *
 * ⚠️ Falsi positivi possibili: chiusura forzata del browser, spegnimento,
 * scheda uccisa dal sistema su mobile. Serve a contare e a capire DOVE, non a
 * dimostrare il singolo caso.
 *
 * Privacy: nessun dato personale. Si manda il PERCORSO senza query string (che
 * puo' contenere termini di ricerca), il messaggio d'errore troncato, e nulla
 * che identifichi la persona.
 *
 * Robustezza: tutto dentro try/catch e con un tetto di eventi per pagina. Una
 * sonda che rompe il sito o inonda GA4 e' peggio di nessuna sonda.
 */
(function () {
  'use strict';

  // Un browser pilotato (Playwright, Puppeteer, Selenium, CDP) si dichiara
  // con `navigator.webdriver`: e' un robot, non un cliente. Per lui la sonda
  // non fa nulla: 191.000 dei 215.000 errori della settimana 15-21/09/2026
  // venivano da un solo crawler. ⚠️ Vale solo con un protocollo di
  // automazione collegato: Chrome headless lanciato a mano (--dump-dom) non
  // lo dichiara e passa. Chi passa lo esclude comunque il rapporto, quando
  // domina le pagine viste.
  try { if (navigator.webdriver) return; } catch (e) {}

  var CHIAVE = 'vcb_nav';
  // Tetto PER TIPO di evento, non complessivo. Con un tetto unico i tre
  // valori di velocità (che partono sempre) esaurirebbero il budget e gli
  // errori non arriverebbero mai — proprio quelli che ci interessano di più.
  var TETTI = {
    vcb_js_error: 8,      // un loop di errori non deve inondare
    vcb_page_crash: 1,
    vcb_empty_results: 1,
    vcb_web_vitals: 3,    // lcp, cls, inp: uno ciascuno
    vcb_taglia_esaurita: 4    // quante taglie prova, non quante volte
  };
  var contati = {};

  /* ⚠️ IL ROBOT DA SOLO FA 27.000 EVENTI AL GIORNO.
     Misurato il 03/11/2026: su 809.492 errori in 30 giorni, il 93% e' UNA
     riga sola — `Failed to execute 'importScripts'` dentro `worker.modern.js`,
     cioe' il worker dei Web Pixel di Shopify che non parte in un browser
     headless. Non e' un guasto del sito: e' lo scraper di Singapore
     (38.185 sessioni in 7 giorni, zero ordini).
     Non si spegne del tutto, perche' se un giorno la firma cambiasse vorremmo
     accorgercene: se ne manda **UNO per caricamento** invece di otto.
     ⚠️ Da oggi il conteggio degli errori NON e' confrontabile con quello di
     prima: cala di circa due terzi senza che nulla sia migliorato. La nota
     sta anche in scripts/site_errors.py, perche' chi legge il rapporto non
     scambi il taglio per una guarigione. */
  var ROBOT_MSG = "Failed to execute 'importScripts'";
  var ROBOT_SRC = 'worker.modern.js';
  var robotVisti = 0;
  function eIlRobot(nome, dati) {
    if (nome !== 'vcb_js_error' || !dati) return false;
    return String(dati.err_msg || '').indexOf(ROBOT_MSG) >= 0 &&
           String(dati.err_src || '').indexOf(ROBOT_SRC) === 0;
  }

  // Versione del tema che ha servito QUESTA pagina (/cdn/shop/t/536/...).
  // Serve a distinguere un asset che manca davvero da uno di una versione
  // precedente, chiesto da una pagina rimasta aperta oltre una pubblicazione.
  var versioneTema = '';
  try {
    var m0 = /\/cdn\/shop\/t\/(\d+)\//.exec((document.currentScript && document.currentScript.src) || '');
    versioneTema = m0 ? m0[1] : '';
  } catch (e) {}

  function percorso() {
    try {
      return (location.pathname || '/').slice(0, 120); // senza query: puo' contenere ricerche
    } catch (e) {
      return '/';
    }
  }

  /**
   * Ultimo segmento di una URL, SENZA query string.
   * ⚠️ La query va tolta anche qui, non solo da `page_path`: un errore dentro
   * uno script inline ha come `filename` la URL della pagina, quindi su
   * /search?q=... finirebbe in GA4 il termine cercato dall'utente.
   * Trovato provando la sonda il 21/08/2026.
   */
  function senzaQuery(u) {
    try {
      return String(u || '').split('?')[0].split('#')[0].split('/').pop().slice(0, 60);
    } catch (e) {
      return '';
    }
  }

  /**
   * Da quale risorsa arriva un errore di caricamento.
   *
   * ⚠️ `senzaQuery` da sola non basta qui, e per due ragioni misurate il
   * 27/08/2026 su 2.095 errori di tipo SCRIPT:
   *
   *   1. il 71% arrivava con sorgente VUOTA. Un `<script>` che fallisce puo'
   *      avere `src` non ancora valorizzato (iniettato e rimosso), e restava
   *      una riga che diceva solo "uno script e' fallito" — inutile.
   *   2. il solo nome del file perde il DOMINIO, che e' il pezzo che conta:
   *      `js` puo' essere di chiunque, `connect.facebook.net/js` no. Quasi
   *      tutte queste risorse sono di terze parti, e sapere di CHI e' il
   *      problema decide se possiamo farci qualcosa.
   *
   * Quando la URL manca si ripiega sull'identita' dell'elemento: id, classe,
   * o il tipo. Meglio "script[async] senza src" che il nulla.
   */
  function origineUrl(u) {
    // ⚠️ Il confronto va fatto su host NORMALIZZATI da entrambi i lati:
    // togliere `www.` solo a uno dei due fa risultare estranee anche le
    // nostre risorse, e ogni riga sprecherebbe 17 caratteri per dire
    // "e' nostra".
    var nudo = function (h) { return String(h || '').replace(/^www\./, ''); };
    var host = '';
    try {
      host = nudo(new URL(u, location.href).host);
    } catch (e) {}
    var f = senzaQuery(u) || '(radice)';
    return (host && host !== nudo(location.host) ? host + '/' : '') + f;
  }

  function origineRisorsa(t) {
    try {
      var u = t.src || t.href || t.currentSrc || '';
      if (u) return origineUrl(u);
      var id = t.id ? '#' + t.id : '';
      var cls = t.className && typeof t.className === 'string'
        ? '.' + t.className.split(/\s+/)[0] : '';
      return ('senza-url ' + String(t.tagName).toLowerCase() + id + cls).slice(0, 60);
    } catch (e) {
      return '';
    }
  }

  // Coda: i guasti capitano anche prima che gtag.js sia pronto (anzi, il crash
  // capita proprio all'inizio). Qui si accumulano e si spurgano appena arriva.
  var coda = [];

  /**
   * ⚠️ Invio diretto a GA4, senza gtag.js (26/09/2026).
   *
   * La sonda ha sempre parlato attraverso `gtag('event', ...)`. Ma su questo
   * negozio `window.gtag` esiste SOLO perche' lo carica Pandectes: i dati veri
   * di GA4, Ads e Merchant arrivano dal Web Pixel di Shopify, che gtag non lo
   * usa affatto. Il giorno in cui togliamo i tag doppi di Pandectes la sonda
   * ammutolirebbe senza che nessuno se ne accorga.
   *
   * Questa funzione rifa' a mano la richiesta che faceva gtag. Formato
   * catturato dal vivo il 26/09/2026 su una pagina collezione:
   *
   *   POST region1.analytics.google.com/g/collect?v=2&tid=G-…&cid=…&sid=…&dl=…
   *   corpo: en=vcb_prova&_ee=1&ep.vitale=inp&epn.valore=123
   *
   * Regole che non si toccano:
   * - `cid` e `sid` si LEGGONO dai cookie che GA4 scrive da solo. Non si
   *   inventano mai: un identificativo inventato creerebbe utenti fantasma e
   *   sporcherebbe le sessioni, che e' peggio del silenzio.
   * - Niente cookie `_ga` ⇒ GA4 non e' attivo su questo browser (consenso
   *   negato, o prima pagina prima del consenso). Si tace.
   * - `ep.` per il testo, `epn.` per i numeri: e' la convenzione di GA4, la
   *   stessa che usava gtag.
   *
   * Effetto collaterale voluto: gli eventi diagnostici smettono di finire
   * anche in Google Ads. Con gtag ogni errore partiva pure verso
   * `ccm/collect` e `rmkt/collect`, compreso l'identificativo `AW-0` che non
   * esiste. Sono statistiche del sito, in Ads non servono a niente.
   */
  var GA4_ID = 'G-V2BZMC4G4H';
  var GA4_URL = 'https://region1.analytics.google.com/g/collect';

  function cookie(nome) {
    try {
      var m = document.cookie.match('(?:^|; )' + nome + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  function identita() {
    // _ga = GA1.1.<cid>            es. GA1.1.1007102284.1790411588
    var p = cookie('_ga').split('.');
    if (p.length < 4) return null;
    // _ga_V2BZMC4G4H = GS2.1.s<sid>$o<numero sessione>$g0$t…
    var s = cookie('_ga_' + GA4_ID.replace(/^G-/, ''));
    var sid = (s.match(/s(\d{8,})/) || [])[1];
    if (!sid) return null;
    return { cid: p[2] + '.' + p[3], sid: sid,
             sct: (s.match(/\$o(\d+)/) || [])[1] || '1' };
  }

  function consensoAnalitico() {
    // Se Shopify espone la Customer Privacy API si ascolta lei. Se non c'e',
    // il cookie `_ga` e' gia' di per se' la prova che GA4 puo' scrivere.
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() !== false;
      }
    } catch (e) { /* nel dubbio si lascia decidere al cookie */ }
    return true;
  }

  function mandaDiretto(nome, dati) {
    try {
      if (!navigator.sendBeacon) return false;
      if (!consensoAnalitico()) return false;
      var io = identita();
      if (!io) return false;
      var q = ['v=2', 'tid=' + GA4_ID, 'cid=' + io.cid, 'sid=' + io.sid,
               'sct=' + io.sct, 'seg=1', '_s=1', '_p=' + Date.now(),
               'dl=' + encodeURIComponent(location.href),
               'dp=' + encodeURIComponent(location.pathname),
               'dt=' + encodeURIComponent((document.title || '').slice(0, 300)),
               'sr=' + screen.width + 'x' + screen.height,
               'ul=' + String(navigator.language || '').toLowerCase()];
      var corpo = ['en=' + encodeURIComponent(nome), '_ee=1'];
      for (var k in dati) {
        if (!Object.prototype.hasOwnProperty.call(dati, k)) continue;
        var v = dati[k];
        if (v === null || v === undefined || v === '') continue;
        corpo.push((typeof v === 'number' ? 'epn.' : 'ep.') + encodeURIComponent(k) +
                   '=' + encodeURIComponent(String(v).slice(0, 100)));
      }
      return navigator.sendBeacon(GA4_URL + '?' + q.join('&'), corpo.join('&')) !== false;
    } catch (e) {
      return false;
    }
  }

  /**
   * Prima gtag se c'e' (nessuna regressione finche' Pandectes resta), invio
   * diretto altrimenti. Mai tutti e due: l'evento partirebbe in doppio.
   * Se non parte per davvero resta in coda e ci riprova il chiamante.
   */
  function spurga() {
    var conGtag = typeof window.gtag === 'function';
    while (coda.length) {
      var ev = coda[0];
      if (conGtag) {
        try { window.gtag('event', ev[0], ev[1]); } catch (e) { /* ignora */ }
      } else if (!mandaDiretto(ev[0], ev[1])) {
        return false;
      }
      coda.shift();
    }
    return true;
  }

  function invia(nome, dati) {
    try {
      if (eIlRobot(nome, dati) && ++robotVisti > 1) return;
      var tetto = TETTI[nome] || 2;
      contati[nome] = (contati[nome] || 0) + 1;
      if (contati[nome] > tetto) return;
      dati = dati || {};
      dati.page_path = percorso();
      coda.push([nome, dati]);

      // ⚠️ NON usare dataLayer.push(['event', ...]).
      // Su questo negozio GA4 e' alimentato dai Web Pixels di Shopify e gtag.js
      // arriva dinamicamente: un push in quel formato NON produce alcuna
      // richiesta di rete. Verificato il 25/08/2026 sparando i tre metodi e
      // guardando il traffico:
      //   gtag('event', ...)            -> POST a /g/collect con tid=G-V2BZMC4G4H
      //   dataLayer.push(['event',...]) -> niente
      //   Shopify.analytics.publish()   -> niente verso GA4
      // Per questo la sonda ha taciuto per 28 giorni, crash compreso.
      if (spurga()) return;

      // gtag non c'e' ancora: riprova a intervalli, poi si arrende.
      var tentativi = 0;
      var t = setInterval(function () {
        if (spurga() || ++tentativi > 40) clearInterval(t);
      }, 250);
    } catch (e) {
      /* la sonda non deve mai far cadere la pagina */
    }
  }

  /* Rumore atteso, non guasti.
     Dopo OGNI pubblicazione del tema gli identificativi delle sezioni cambiano.
     Una pagina aperta prima della pubblicazione, quando prova a idratare il
     terzo livello del menu, chiede una sezione che non esiste piu' e
     `section-renderer.js` solleva un errore. Non e' un difetto: e' previsto,
     l'utente vede semplicemente il menu a due livelli.
     Misurato il 04/09/2026: 2.522 + 2.391 errori in un giorno con quattro
     pubblicazioni, contro 14 in un giorno senza. Contarli sporca la misura e
     nasconde i guasti veri.
     ⚠️ Se un giorno questi errori comparissero SENZA una pubblicazione,
     vorrebbe dire che il render delle sezioni e' rotto davvero: togliere
     questo filtro e guardare. */
  function attesoDopoPubblicazione(msg) {
    if (!msg) return false;
    if ((msg.indexOf('Section ') === 0 && msg.indexOf('not found') > 0) ||
        msg.indexOf('No empty section markup found') === 0) return true;
    /* Stessa famiglia, altra voce. Shopify inietta un polyfill degli import
       map (es-modules-shim) per i browser senza supporto nativo: quello
       ricarica i moduli del tema con fetch, e se la pagina e' di una versione
       precedente a una pubblicazione il CDN risponde 403. Ne escono
         "Unable to fetch https://.../cdn/shop/t/533/assets/morph.js"
         "403 Forbidden https://.../cdn/shop/t/534/assets/focus.js?v="
       Misurati il 03-04/09/2026: ~16.000 in due giorni con tre pubblicazioni,
       zero nei giorni senza. Si tace SOLO se la versione nel messaggio non e'
       quella della pagina: un 403 sulla versione corrente e' un guasto vero. */
    var mv = /\/cdn\/shop\/t\/(\d+)\//.exec(msg);
    return !!(mv && versioneTema && mv[1] !== versioneTema &&
              (msg.indexOf('Unable to fetch') === 0 || /^\d{3} /.test(msg)));
  }

  /* Browser senza import map (Safari < 16.4, iOS 16.0-16.3, i Mac fermi a
     Catalina): il tentativo NATIVO di risolvere '@theme/...' fallisce e
     finisce qui, poi il polyfill iniettato da Shopify rifa' il lavoro e la
     pagina funziona. 772 errori in tre giorni (05-07/09/2026) da ~50 sessioni,
     il secondo messaggio piu' frequente: non c'e' niente da correggere, e
     ognuno consumava un terzo del tetto degli errori di quella pagina.
     ⚠️ Se Shopify togliesse il polyfill, questi utenti avrebbero un sito
     morto e noi non lo vedremmo: la riga da riaccendere e' questa. */
  function attesoDalBrowser(msg) {
    if (msg.indexOf("TypeError: Module specifier, '@theme/") === 0) return true;
    /* ⚠️ 13/10/2026 — due artefatti delle WEBVIEW, non difetti nostri.
       Misurati su 14 giorni in Italia, erano il primo e il quarto messaggio
       piu' frequente: insieme ~800 al giorno, piu' di tutto il resto messo
       insieme, e nessuno dei due e' codice nostro.

       `_AutofillCallbackHandler` (10.073 eventi / 2.280 utenti) lo inietta il
       browser in-app di iOS per il riempimento automatico dei moduli: la
       variabile viene cercata prima di essere definita e l'eccezione finisce
       nel nostro `window.onerror`. Non c'e' niente da correggere e la pagina
       funziona.

       `Error invoking postMessage: Java object is gone` (1.411 / 801) e' il
       gemello Android: la WebView ha gia' distrutto il ponte Java quando un
       listener prova a scriverci. Arriva da
       `navigation_performance_logger_android`, che non e' nostro.

       ⚠️ Entrambi consumavano il tetto di 8 errori per pagina: un difetto
       vero sulla stessa pagina poteva restare fuori. */
    if (msg.indexOf('_AutofillCallbackHandler') >= 0) return true;
    if (msg.indexOf('Java object is gone') >= 0) return true;
    return false;
  }

  /* Stessa famiglia, altra forma (09/09/2026). Nei browser senza import map
     (Safari 14-15, 604 = WebKit vecchio) non fallisce solo la risoluzione
     di '@theme/...': fallisce il CARICAMENTO nativo di ogni <script
     type="module"> del tema — header.js, cart-icon.js, announcement-bar.js,
     ~10 file per pagina, ~100-300 «SCRIPT» al giorno tutti da quei Safari —
     e le promesse «Importing a module script failed.». Poi il polyfill di
     Shopify (es-modules-shim) li ricarica e la pagina funziona. Si tace SOLO
     se il browser dichiara di non avere gli import map: su un browser
     moderno un modulo che non carica e' un guasto vero e resta visibile. */
  var senzaImportMap = false;
  try {
    senzaImportMap = !(window.HTMLScriptElement && HTMLScriptElement.supports &&
                       HTMLScriptElement.supports('importmap'));
  } catch (e) {}

  // <img>/<script>/<link> che non caricano sono "risorsa". Un evento `error`
  // su un altro elemento (es. <shopify-account>, componente di Shopify) e' il
  // componente che segnala un guasto suo: va contato a parte, non come un
  // file mancante.
  var RISORSE = /^(IMG|SCRIPT|LINK|VIDEO|AUDIO|SOURCE|IFRAME|OBJECT|EMBED|TRACK)$/;

  /* ⚠️ 28/09/2026 — un caricamento INTERROTTO non e' un guasto.
     Il browser emette `error` su una risorsa anche quando la richiesta viene
     annullata: chi lascia la pagina mentre le immagini pigre stanno ancora
     arrivando genera un errore per ognuna. Su una scheda prodotto ci sono
     ~50 immagini e meta' sono pigre, quindi una singola uscita ne produce
     parecchie.

     Misurato: su tre schede prodotto, `naturalWidth === 0` su ZERO immagini
     (0 rotte su ~50 per pagina), mentre la rete mostrava `net::ERR_ABORTED`.
     E le 14 URL «rotte» piu' frequenti nel rapporto rispondono tutte 200.
     Erano 544 falsi guasti su immagini perfettamente sane.

     Due filtri, entrambi sul lato sicuro — se sbagliamo, TACIAMO un errore
     invece di inventarne uno:
     1. la pagina se ne sta andando (`pagehide`, o nascosta);
     2. l'elemento non e' piu' nel documento (rimosso mentre caricava). */
  var inUscita = false;
  function segnaUscita() { inUscita = true; }
  window.addEventListener('pagehide', segnaUscita);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') segnaUscita();
  });

  function caricamentoAnnullato(t) {
    try {
      if (inUscita) return true;
      // `isConnected` non esiste sui browser piu' vecchi: nel dubbio si conta.
      if (typeof t.isConnected === 'boolean' && !t.isConnected) return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  /* Il censimento delle risorse rotte: UNA volta, a pagina ferma.
     Un'immagine annullata e una rotta si somigliano sull'istante (entrambe
     `complete` con `naturalWidth === 0`), ma non dopo: l'annullata non c'e'
     piu' perche' la pagina e' andata via, la rotta resta. Quindi si guarda
     dopo, non durante.
     Misurato il 30/09 su tre schede prodotto: immagini davvero rotte **ZERO**
     su circa cinquanta per pagina, mentre la rete mostrava `net::ERR_ABORTED`
     — e le 14 URL «rotte» piu' frequenti del rapporto rispondevano tutte 200. */
  function censimentoRisorse() {
    try {
      if (inUscita || document.visibilityState !== 'visible') return;
      var rotte = [], n = 0;
      var imgs = document.images || [];
      for (var i = 0; i < imgs.length; i++) {
        var im = imgs[i];
        if (!im.currentSrc || !im.complete || im.naturalWidth > 0) continue;
        n++;
        if (rotte.length < 3) rotte.push(origineRisorsa(im));
      }
      if (!n) return;
      invia('vcb_js_error', {
        err_tipo: 'risorsa',
        err_msg: 'IMG',
        err_src: rotte.join(' | ').slice(0, 100),
        quante: n
      });
    } catch (e) { /* la sonda non deve mai far cadere la pagina */ }
  }
  /* Si aggancia a `load`, ma se il documento e' GIA' completo (la sonda
     caricata tardi, o un ripristino da bfcache) quell'evento non arriva piu'
     e il censimento non partirebbe mai. */
  function programmaCensimento() {
    var poi = function () { setTimeout(censimentoRisorse, 3000); };
    /* ⚠️ `requestIdleCallback` SENZA scadenza puo' non scattare mai: su una
       collezione con 157 immagini e gli script di terzi il thread non va mai
       in pausa, e il censimento resta in coda per sempre. Provato: niente
       evento dopo 6,5 secondi. `timeout` lo fa partire comunque.
       E' lo stesso difetto del `defer` sul caricatore dell'accessibilita':
       una cosa rinviata che non parte piu' tace, non si lamenta. */
    if (window.requestIdleCallback) {
      requestIdleCallback(poi, { timeout: 2000 });
    } else {
      poi();
    }
  }
  if (document.readyState === 'complete') {
    programmaCensimento();
  } else {
    window.addEventListener('load', programmaCensimento);
  }

  /* ---------------------------------------------- 1. errori JavaScript */
  window.addEventListener('error', function (ev) {
    try {
      if (!ev) return;
      // Gli errori di caricamento risorsa (img/script) non hanno `message`:
      // sono rumore diverso, li marchiamo a parte.
      if (!ev.message && ev.target && ev.target.tagName) {
        var tag = String(ev.target.tagName).slice(0, 20);
        if (senzaImportMap && tag === 'SCRIPT' && String(ev.target.type || '') === 'module') return;
        if (caricamentoAnnullato(ev.target)) return;
        /* ⚠️ 01/10/2026 — i guasti di RISORSA non si contano piu' qui.
           I due filtri del 28/09 prendono solo l'uscita dopo `pagehide` e
           l'elemento rimosso. **Non prendono l'annullamento mentre la pagina
           e' ancora visibile**: tocco su un link in Safari, `window.stop()`,
           memoria. Misurato: con `window.stop()` a pagina visibile arrivavano
           **20 eventi** su immagini perfettamente sane, con `inUscita=false`,
           `isConnected=true` e `visibilityState='visible'`.
           Erano il **91%** di tutti gli errori che restavano classificati
           «nostri» — IMG, LINK e SCRIPT insieme — e i 5-6 moduli del tema
           comparivano con conteggi quasi identici: era UN caricamento
           interrotto, non cinque file rotti.
           Ora si fa un CENSIMENTO a pagina ferma (vedi `censimentoRisorse`):
           a quel punto un annullamento non c'e' piu', e chi e' davvero rotto
           si vede. Uno script che non carica per davvero si fa sentire
           comunque, come eccezione JavaScript quando il suo codice serve. */
        if (RISORSE.test(tag)) return;
        invia('vcb_js_error', {
          err_tipo: 'elemento',
          err_msg: tag,
          err_src: origineRisorsa(ev.target)
        });
        return;
      }
      var m = String(ev.message || '');
      if (attesoDopoPubblicazione(m) || attesoDalBrowser(m)) return;
      invia('vcb_js_error', {
        err_tipo: 'script',
        err_msg: m.slice(0, 100),
        err_src: senzaQuery(ev.filename) + ':' + (ev.lineno || 0)
      });
    } catch (e) {}
  }, true);

  window.addEventListener('unhandledrejection', function (ev) {
    try {
      var r = ev && ev.reason;
      var mp = String((r && (r.message || r)) || '');
      var src = '';
      // Una XHR fallita rifiuta con un ProgressEvent, che stampato dice solo
      // "[object ProgressEvent]": 32.000 righe mute il 04/09/2026. Si prende
      // il tipo (error/abort/timeout: un abort e' l'utente che se ne va, non
      // un guasto) e la URL, quando il browser l'ha gia' valorizzata.
      try {
        var xhr = r && r.target && typeof XMLHttpRequest !== 'undefined' &&
                  r.target instanceof XMLHttpRequest ? r.target : null;
        if (xhr) {
          // vale anche per XMLHttpRequestProgressEvent di Safari
          mp = 'XHR fallita (' + (r.type || 'error') + ')';
          var u = xhr.responseURL || '';
          src = u ? origineUrl(u) : '';
        }
        // Un Error rifiutato porta lo stack: la prima URL .js che contiene
        // e' il file colpevole. Senza, «Cannot read properties of undefined
        // (reading 'M_ID')» (15 volte sul carrello, 08-09/09/2026) resta
        // senza mittente: non sta in nessuno script statico della pagina.
        if (!src && r && typeof r.stack === 'string') {
          // qualunque schema: anche chrome-extension:// e blob:, che sono
          // la firma di un'estensione o di un'app iniettata (M_ID, 10/09/2026)
          var ms = /[a-z][a-z0-9+.-]*:\/\/[^\s)]+\.js[^\s):]*/i.exec(r.stack);
          if (ms) src = origineUrl(ms[0]);
        }
      } catch (e) {}
      if (attesoDopoPubblicazione(mp)) return;
      if (senzaImportMap && mp.indexOf('Importing a module script failed') === 0) return;
      invia('vcb_js_error', {
        err_tipo: 'promessa',
        err_msg: mp.slice(0, 100),
        err_src: src
      });
    } catch (e) {}
  });

  /* ------------------------------------- 2. la pagina prima e' morta? */
  /* ⚠️ Solo quando la pagina e' VISIBILE (15/09/2026). Safari (e Chrome)
     caricano pagine in sottofondo che l'utente potrebbe non vedere mai: il
     «Top Hit» mentre si digita l'indirizzo, il prerendering. Quelle pagine
     eseguono lo script, piantavano il segnale e, scartate senza `pagehide`,
     facevano contare un crash alla pagina successiva. Misurato 08-14/09: la
     home «moriva» 1 volta ogni 10-16 aperture mobile, nel 94% dei casi con
     un'ALTRA pagina aperta entro 5 s e con le sorgenti dirette al 45% dei
     crash contro il 17% del traffico — la firma del precaricamento, non
     della memoria. Ora si arma e si giudica solo alla prima visibilita'. */
  function armaSegnale() {
    try {
      var prec = sessionStorage.getItem(CHIAVE);
      if (prec) {
        var p = JSON.parse(prec);
        // Se il segnale e' ancora qui, `pagehide` non e' mai scattato.
        // 08/10/2026 — `crash_nav`: il crash e' vero o e' un difetto di
        // misura? I crash NON seguono il peso della pagina (le pagine leggere
        // ne fanno 10 volte piu' delle schede prodotto, che sono le piu'
        // pesanti): se fosse memoria, l'ordine sarebbe rovesciato.
        // Il tipo di navigazione di QUESTA pagina — quella che segnala —
        // distingue i due casi:
        //   reload       il browser ha riavviato da se' la stessa pagina:
        //                e' la firma del crash vero su Safari
        //   navigate     il cliente e' andato altrove e `pagehide` non e'
        //                scattato: NON e' un crash, e' un falso positivo
        //   back_forward tasto indietro
        var navTipo = '';
        try {
          navTipo = (performance.getEntriesByType('navigation')[0] || {}).type || '';
        } catch (x) {}
        /* ⚠️ 13/10/2026 — il filtro ora si applica QUI, non in lettura.
           `crash_nav` esiste dall'08/10 e dice la verita', ma la sonda
           continuava a mandare TUTTI i casi lasciando il filtro a chi legge
           i dati — e chi legge non lo applicava. Misurato sulla finestra in
           cui il parametro esiste: `navigate` + `back_forward` = 288 eventi
           su 227 utenti, `reload` = 11 su 7. **Il 96,5% dei «crash» erano
           cambi di pagina**, e il numero gonfiato e' stato riportato come
           reale due volte, l'ultima oggi.
           Si manda solo `reload` (la firma del crash vero) e il caso in cui
           il browser non sa dirlo (~3,5%: meglio un dubbio che un buco). */
        if (navTipo === 'navigate' || navTipo === 'back_forward') {
          /* non e' un crash: la pagina prima e' stata lasciata, non e' morta */
        } else {
          invia('vcb_page_crash', {
            crash_page: String(p.p || '').slice(0, 120),
            crash_durata_s: p.t ? Math.round((Date.now() - p.t) / 1000) : 0,
            crash_nav: navTipo || '(ignoto)'
          });
        }
      }
      sessionStorage.setItem(CHIAVE, JSON.stringify({ p: percorso(), t: Date.now() }));
    } catch (e) {
      /* sessionStorage puo' essere negato (navigazione privata, cookie bloccati) */
    }
  }
  try {
    var nascosta = document.prerendering || document.visibilityState !== 'visible';
    if (!nascosta) {
      armaSegnale();
    } else {
      var armato = false;
      var quandoVisibile = function () {
        if (armato) return;
        if (document.prerendering || document.visibilityState !== 'visible') return;
        armato = true;
        armaSegnale();
      };
      document.addEventListener('visibilitychange', quandoVisibile);
      document.addEventListener('prerenderingchange', quandoVisibile);
    }
    window.addEventListener('pagehide', function () {
      try {
        sessionStorage.removeItem(CHIAVE);
      } catch (e) {}
    });
  } catch (e) {}

  /* --------------------------------------- 3. pagine senza risultati */
  window.addEventListener('load', function () {
    try {
      var p = percorso();
      if (p.indexOf('/collections/') !== 0 && p.indexOf('/search') !== 0) return;
      // Si guarda la griglia dei risultati, non tutta la pagina: i caroselli
      // di raccomandazione contengono prodotti anche quando la griglia e' vuota.
      var griglia = document.querySelector('#ResultsList, .main-collection-grid');
      if (!griglia) return;
      if (griglia.querySelectorAll('a[href*="/products/"]').length === 0) {
        invia('vcb_empty_results', { tipo: p.indexOf('/search') === 0 ? 'ricerca' : 'collezione' });
      }
    } catch (e) {}
  });

  // ------------------------------------------------------------------
  // VELOCITÀ REALE e INCEPPI
  //
  // Fino a oggi non avevamo NESSUN dato di velocità percepita dagli utenti
  // veri: PageSpeed richiede una chiave, il browser automatizzato non
  // dipinge davvero. Ma la lentezza è la causa silenziosa numero uno
  // dell'abbandono, e non si vede in nessuna metrica di GA4.
  //
  // Qui si misura sul campo, senza librerie:
  //   LCP  quanto ci mette a comparire la cosa piu' grande della pagina
  //   CLS  quanto il contenuto salta mentre carichi
  //   INP  quanto tarda la pagina a rispondere a un tocco
  //
  // I valori vanno anche in fasce ("buono/da_migliorare/scarso") perché una
  // fascia è una DIMENSIONE (basta registrarla), mentre una media richiede
  // una metrica personalizzata. Così il dato è leggibile subito.
  //
  // RAGE CLICK: tre tocchi ravvicinati nello stesso punto. È il gesto di chi
  // preme un bottone che non risponde — l'inceppo allo stato puro.
  // ------------------------------------------------------------------
  function fascia(nome, v) {
    if (nome === 'lcp') return v <= 2500 ? 'buono' : (v <= 4000 ? 'da_migliorare' : 'scarso');
    if (nome === 'inp') return v <= 200 ? 'buono' : (v <= 500 ? 'da_migliorare' : 'scarso');
    return v <= 0.1 ? 'buono' : (v <= 0.25 ? 'da_migliorare' : 'scarso');
  }

  var vitali = {};

  /**
   * Un'etichetta breve e stabile per l'elemento colpito da un'interazione.
   *
   * Serve stabile, non precisa: le classi generate dal tema cambiano a ogni
   * build (`search-page-input--AL3krRl`), e usarle intere produrrebbe una
   * riga diversa per ogni utente invece di un conteggio. Si tiene il tag, l'id
   * e la PRIMA classe, tagliata prima del trattino doppio.
   */
  function bersaglio(n) {
    try {
      if (!n || !n.tagName) return '(rimosso)';
      var id = n.id ? '#' + n.id.split('--')[0] : '';
      var c = '';
      if (n.className && typeof n.className === 'string') {
        c = '.' + n.className.trim().split(/\s+/)[0].split('--')[0];
      }
      return (n.tagName.toLowerCase() + id + c).slice(0, 46);
    } catch (e) {
      return '(illeggibile)';
    }
  }

  /**
   * Dove se ne va il tempo di un'interazione lenta.
   *
   * Sapere CHE COSA l'utente ha toccato non basta: da agosto sappiamo che il
   * bersaglio peggiore e' `img.product-media__image`, e tre tentativi di
   * correzione (decodifica immagini, pannello accessibilita', gallerie
   * differite) non hanno mosso l'INP di un punto. Mancava il PERCHE'.
   *
   * `PerformanceEventTiming` divide l'attesa in tre pezzi, e ognuno indica
   * una cura diversa:
   *
   *   attesa        startTime → processingStart
   *                 il thread era occupato ALTROVE quando l'utente ha toccato.
   *                 Cura: togliere lavoro dal thread (script di terzi,
   *                 idratazione, cicli lunghi).
   *   elaborazione  processingStart → processingEnd
   *                 il nostro gestore d'evento e' lento. Cura: il codice del
   *                 gestore.
   *   disegno       processingEnd → fine
   *                 layout e pittura dopo il gestore. Cura: CSS, DOM enorme,
   *                 immagini non dimensionate.
   *
   * Le tre fasi si ricavano da dati che esistono anche su Safari.
   */
  function osserva(tipo, callback, extra) {
    try {
      var po = new PerformanceObserver(function (l) { l.getEntries().forEach(callback); });
      var opz = { type: tipo, buffered: true };
      if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) opz[k] = extra[k];
      po.observe(opz);
      return po;
    } catch (e) { return null; }
  }

  try {
    osserva('largest-contentful-paint', function (e) { vitali.lcp = e.startTime; });
    osserva('layout-shift', function (e) {
      if (e.hadRecentInput) return;
      vitali.cls = (vitali.cls || 0) + e.value;
      /* Il DETTAGLIO di chi si sposta e' stato tolto il 03/11/2026: aveva una
         condizione d'uscita scritta («entro il 18/10 o appena il colpevole e'
         noto») e la domanda e' chiusa — il CLS di campo e' 0,000 col 99% di
         buone su telefono e 0,010 col 94% su desktop. Restava a misurare un
         problema che non c'e' piu'. Se un giorno il CLS risale, il blocco sta
         in git: `git log -S cls_dettaglio`. */
    });
    // ⚠️ Sapere che l'INP è scarso non serve a correggerlo: serve sapere CHE
    // COSA l'utente stava toccando. Il 27/08/2026 la pagina /search risultava
    // al 26% di interazioni scarse (tre volte la media del sito) e le ipotesi
    // possibili erano quattro — ricerca predittiva, filtri, scroll della
    // griglia, apertura del pannello. Senza il bersaglio si sceglie a caso.
    //
    // `PerformanceEventTiming` porta sia il nome dell'evento (`pointerdown`,
    // `keydown`, `click`) sia il nodo colpito. Il nodo può essere già stato
    // rimosso dal DOM quando lo leggiamo: `bersaglio()` regge il null.
    /* ⚠️ RISCRITTO IL 29/09/2026 — la versione precedente misurava la cosa
       sbagliata, e la sbagliava sempre nello stesso verso.

       Teneva la voce `event` con la `duration` massima, senza guardare
       `interactionId`. Ma `duration` si misura fino al PRIMO DISEGNO dopo
       l'elaborazione, e tutte le voci di una stessa interazione finiscono a
       quel disegno: vinceva quindi sempre quella con lo `startTime` piu'
       PRECOCE. In una sequenza di tocco l'ordine e'
       `pointerover → pointerdown → pointerup → click`, e nessuno ascolta
       `pointerover`. Risultato: `processingEnd - processingStart` era ~0 PER
       COSTRUZIONE, e il tempo del gestore del `click` — che cade dopo quel
       `processingEnd` — finiva contato come «disegno».

       Misurato su 40.294 misure in 14 giorni: l'87,2% erano eventi che l'INP
       di Google NON CONTA AFFATTO (`interactionId = 0`), e i `click` scelti
       erano **UNO**. La conclusione «i nostri gestori sono innocenti allo
       0,02%» era l'ombra di `pointerover`, non un dato.

       Ora, come fa la libreria ufficiale `web-vitals`:
       - si scartano le voci con `interactionId = 0`: non sono interazioni;
       - si RAGGRUPPA per `interactionId`, perche' un tocco produce piu' voci;
       - attesa      = processingStart della PRIMA voce − il suo startTime
       - elaborazione = processingEnd dell'ULTIMA − processingStart della PRIMA
                        (cosi' comprende TUTTI i gestori, click incluso)
       - disegno     = fine dell'interazione − processingEnd dell'ULTIMA
       - `durationThreshold: 16` per non perdere le interazioni brevi.

       Si conserva il BERSAGLIO come stringa, non il nodo: tenere i nodi vivi
       in una mappa li sottrae al recupero della memoria, e su questo sito i
       crash sono proprio da memoria esaurita. */
    /* ⚠️ TERZA VERSIONE, 01/10/2026. Le prime due misuravano la cosa sbagliata.
       Si tiene qui la storia perche' l'errore non si rifaccia.

       v1: teneva la voce `event` con `duration` massima senza guardare
           `interactionId`. Vinceva sempre `pointerover`, che nessuno ascolta,
           quindi l'elaborazione risultava ~0 PER COSTRUZIONE.
       v2: raggruppava per `interactionId` ma calcolava l'INP come
           `max(startTime+duration) - min(startTime)` e l'elaborazione come
           `max(processingEnd) - min(processingStart)`. **Entrambe includono il
           tempo in cui il dito STA SULLO SCHERMO**, cioe' il buco fra la fine
           del gestore di `pointerdown` e l'inizio di quello di `pointerup`.
           Provato a tavolino: dito premuto 600 ms con gestori da 2 ms dava
           «INP 648 ms, elaborazione 604 ms», dove Google conta **48**.
           Spiega anche perche' il nostro p75 diceva 260 e Google 211.

       v3, come fa Google: l'INP di un'interazione e' la **durata massima fra
       le sue voci**, non l'arco da inizio a fine. E le tre fasi si leggono da
       QUELLA voce, cosi' sommano esattamente alla durata e nessun tempo di
       pressione puo' entrarci.

       attesa       = processingStart - startTime
       elaborazione = processingEnd - processingStart
       disegno      = (startTime + duration) - processingEnd */
    /* ⚠️ CORRETTO IL 17/10/2026 — le tre fasi mentivano, e sempre nello
       stesso verso.

       `a`, `e`, `d` erano calcolati sulla voce VINCENTE, non sull'interazione
       raggruppata, nonostante il commento qui sopra prometta l'algoritmo di
       `web-vitals`. La riscrittura del 29/09 aveva corretto QUALE voce vince,
       non COME si dividono le fasi.

       Per una voce `pointerup`, che un gestore non ce l'ha, `e` e' ~0 per
       costruzione e `d = (startTime + duration) - processingEnd` inghiotte
       TUTTO il JavaScript che gira dopo — il gestore del `click` compreso.
       Prova sperimentale: un ascoltatore `click` che brucia 150 ms di JS puro
       e dipinge ZERO produce `click: e=151 d=0,6` e `pointerup: e=0 d=151,6`.
       Gli stessi 150 ms, due etichette opposte. Il 42,7% delle interazioni
       scarse portava l'etichetta «disegno» da questa regola.

       COSA SI E' SCELTO DI FARE, E PERCHE' COSI'.
       Le lettere minuscole `a e d` restano IDENTICHE a prima: `inp_campo.py`
       ricava dalla loro somma il p75, la quota di buone, il semaforo e la
       definizione stessa di «scarse», e la serie storica dal 01/10 deve
       restare confrontabile. Accanto si aggiungono `A E D` MAIUSCOLE, con la
       divisione giusta: `E` somma i gestori di TUTTE le voci di quella
       interazione, `D` e' cio' che resta.

       Vanno nello STESSO campo `dettaglio` e non in uno nuovo perche' una
       dimensione nuova andrebbe registrata a mano in GA4 (che non fa
       backfill) e perche' il lettore e' gia' al tetto delle 9 dimensioni.
       Arrotondate a 8 ms: servono a dire «JavaScript o disegno», non a
       sommare, e senza arrotondamento la cardinalita' del campo esplode e la
       lettura si tronca. A+E+D percio' NON fa l'INP: la somma e' a+e+d. */
    var interazioni = {};
    var quanteInterazioni = 0;
    var OTTO = function (v) { return Math.max(0, Math.round(v / 8) * 8); };
    osserva('event', function (e) {
      try {
        if (!e.interactionId) return;          // 0 = non e' un'interazione
        var k = 'i' + e.interactionId;
        var r = interazioni[k];
        if (!r) {
          if (quanteInterazioni > 300) return; // tetto: non si cresce senza fine
          quanteInterazioni++;
          r = interazioni[k] = { dur: 0, ela: 0, att: 0, ps: 0, pe: 0, st: 0, dove: '' };
        }
        // i gestori di OGNI voce dell'interazione, non solo della vincente
        r.ela += (e.processingEnd - e.processingStart);

        if (e.duration > r.dur) {
          r.dur = e.duration;
          r.st = e.startTime;
          r.ps = e.processingStart;
          r.pe = e.processingEnd;
          /* Il bersaglio: `click` e `keydown` dicono cosa ha fatto l'utente
             meglio di `pointerup`. Se la voce peggiore e' un'altra si tiene la
             sua, ma si annota il nome vero dell'evento. */
          r.dove = String(e.name || '') + ' ' + bersaglio(e.target);
        }

        /* Si riscrive anche quando l'interazione gia' in testa cresce: le voci
           arrivano in ordine sparso e `ela` puo' aumentare DOPO che la
           vincente e' stata scelta. Senza questo ramo, l'ultima voce di una
           interazione lenta non verrebbe mai contata. */
        if (r.dur < (vitali.inp || 0) || (r.dur === (vitali.inp || 0) && vitali.inp_k !== k)) return;

        vitali.inp = r.dur;
        vitali.inp_k = k;
        vitali.inp_dove = r.dove;

        var att = Math.round(r.ps - r.st);
        var ela = Math.round(r.pe - r.ps);
        var dis = Math.round((r.st + r.dur) - r.pe);

        var tot = Math.round(r.dur);
        var attG = Math.max(0, att);
        var elaG = Math.min(Math.round(r.ela), Math.max(0, tot - attG));
        var disG = Math.max(0, tot - attG - elaG);

        vitali.inp_numeri = 'a=' + att + ' e=' + ela + ' d=' + dis +
                            ' A=' + OTTO(attG) + ' E=' + OTTO(elaG) + ' D=' + OTTO(disG);
        vitali.inp_fase = (attG >= elaG && attG >= disG) ? 'attesa'
                        : (elaG >= disG ? 'elaborazione' : 'disegno');
      } catch (x2) { /* la sonda non deve mai far cadere la pagina */ }
    }, { durationThreshold: 16 });
  } catch (e) { /* browser vecchio: si rinuncia in silenzio */ }

  // Si spedisce a pagina che se ne va: prima i valori non sono definitivi.
  var vitaliInviati = false;
  function inviaVitali() {
    if (vitaliInviati) return;
    vitaliInviati = true;
    try {
      Object.keys(vitali).forEach(function (k) {
        // `inp_dove` non è una metrica: viaggia ATTACCATA a `inp`. Se finisse
        // nel ciclo come chiave a sé, GA4 riceverebbe un quarto "vitale"
        // fantasma con un valore non numerico.
        // `inp_dove` e `inp_fase` non sono metriche: viaggiano
        // ATTACCATE a `inp`. Se finissero nel ciclo come chiavi a se', GA4
        // riceverebbe altrettanti "vitali" fantasma con valori non numerici.
        if (k === 'inp_dove' || k === 'inp_fase' || k === 'inp_numeri' ||
            k === 'cls_max') return;
        var v = k === 'cls' ? Math.round(vitali[k] * 1000) / 1000 : Math.round(vitali[k]);
        var dati = { vitale: k, valore: v, giudizio: fascia(k, vitali[k]) };
        if (k === 'inp' && vitali.inp_dove) dati.elemento = vitali.inp_dove;
        if (k === 'inp' && vitali.inp_fase) dati.fase = vitali.inp_fase;
        if (k === 'inp' && vitali.inp_numeri) dati.dettaglio = vitali.inp_numeri;
        invia('vcb_web_vitals', dati);
      });
    } catch (e) { /* ignora */ }
  }
  window.addEventListener('pagehide', inviaVitali);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') inviaVitali();
  });

  // ------------------------------------------------------------------
  // TAGLIA ESAURITA — non un guasto, un SEGNALE DI DOMANDA
  //
  // Il sito funziona: cliccare una taglia esaurita la seleziona e fa comparire
  // «Avvisami quando disponibile» (verificato sul tema live il 02/09/2026).
  // Ma lasciare la mail costa uno sforzo, cliccare no: il modulo raccoglie
  // ~296 richieste al mese, mentre i clic su taglie mancanti sono con ogni
  // probabilita' molti di piu'.
  //
  // Serve a due cose: sapere quali taglie la gente cerca e non trova (lista di
  // riacquisto), e misurare quanti di quei clic si trasformano in una mail.
  //
  // Si ascolta `change` e non `click`: il click puo' arrivare sull'etichetta,
  // sullo swatch o sul testo, mentre `change` scatta una volta sola e solo
  // quando la selezione cambia davvero.
  // ------------------------------------------------------------------
  try {
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.tagName !== 'INPUT') return;
      if (t.getAttribute('data-option-available') !== 'false') return;
      if (!t.checked) return;
      var titolo = document.querySelector('h1');
      invia('vcb_taglia_esaurita', {
        // il percorso identifica il prodotto meglio del titolo: e' stabile,
        // senza spazi e corrisponde a quello del feed
        prodotto: (location.pathname.split('/products/')[1] || location.pathname)
          .split('?')[0].slice(0, 80),
        taglia: String(t.value || '').slice(0, 24),
        variante: t.getAttribute('data-variant-id') || '',
        titolo: titolo ? titolo.textContent.trim().replace(/\s+/g, ' ').slice(0, 60) : ''
      });
    }, true);
  } catch (e) { /* ignora */ }

})();
